/**
 * Public Deal Report Component
 * KOPS-416: Displays shareable deal reports on chat.realestate without authentication
 */

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Row, Col, Card, Alert, Spinner } from 'react-bootstrap';
import { FaClock, FaHome, FaEnvelope, FaPrint } from 'react-icons/fa';
import axiosInstance from '../helpers/api';
import DealAnalysisReadOnly from '../components/deals/DealAnalysisReadOnly';
import { DealDetail } from '../services/dealService';
import NavigationHeader from './public-portal/components/NavigationHeader';
import FooterWithPolicies from './public-portal/components/FooterWithPolicies';
import './PublicDealReport.css';

interface ShareLinkData {
  expiresAt: string;
  accessedCount: number;
  createdAt: string;
}

interface AgentInfo {
  name: string;
  email: string;
  phone?: string;
  company: string;
  brokerageLogo?: string;
}

interface CompPhotoData {
  images: string[];
  address: string;
  bedrooms: number;
  bathrooms: number;
  livingArea: number;
  description: string;
  listingAgent: {
    name: string;
    officeName: string;
  };
  yearBuilt: number | null;
  propertyType: string;
  lotSize: number;
  daysOnMarket: number;
}

interface DealReportData {
  deal: DealDetail;
  compDetails: any[];
  shareLink: ShareLinkData;
  agentInfo?: AgentInfo;
  mlsPhotos?: string[];
  compPhotos?: { [listingId: string]: CompPhotoData };
  listingAgent?: {
    name: string;
    officeName: string;
  };
  propertyDetails?: {
    propertyType: string;
    daysOnMarket: number;
    lotSize: number;
  };
}

const PublicDealReport: React.FC = () => {
  const { shareId } = useParams<{ shareId: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reportData, setReportData] = useState<DealReportData | null>(null);

  useEffect(() => {
    fetchDealReport();
  }, [shareId]);

  const fetchDealReport = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use public endpoint - no auth needed
      const response = await axiosInstance.get(`/deals/public/report/${shareId}`);
      
      if (response.data.success && response.data.data) {
        setReportData(response.data.data);
      } else {
        setError('Unable to load report. Please try again.');
      }
    } catch (err: any) {
      console.error('Error fetching public deal report:', err);
      if (err.response?.status === 404) {
        setError('This share link is no longer valid. Please request a new link from your real estate professional.');
      } else {
        setError('Unable to load report. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="public-report-loading">
        <Container>
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3">Loading investment property analysis...</p>
          </div>
        </Container>
      </div>
    );
  }

  if (error || !reportData) {
    return (
      <div className="public-report-error">
        <Container>
          <Row className="justify-content-center py-5">
            <Col md={6}>
              <Alert variant="danger">
                <h4>Unable to Load Report</h4>
                <p>{error || 'An unexpected error occurred.'}</p>
              </Alert>
              <div className="text-center">
                <a href="https://chat.realestate" className="btn btn-primary">
                  Visit Chat.RealEstate
                </a>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  const expiresAt = new Date(reportData.shareLink.expiresAt);
  const daysRemaining = Math.ceil((expiresAt.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));

  return (
    <div className="public-deal-report">
      {/* Use the same navigation header for consistency */}
      <NavigationHeader />
      
      {/* Agent info and print button bar */}
      <div className="report-actions-bar no-print">
        <Container>
          <Row className="align-items-center py-2">
            <Col>
              {reportData.agentInfo && (
                <div className="agent-info-compact">
                  <span className="text-muted">
                    Presented by: <strong>{reportData.agentInfo.name}</strong> 
                    {reportData.agentInfo.company && ` | ${reportData.agentInfo.company}`}
                    {reportData.agentInfo.phone && ` | ${reportData.agentInfo.phone}`}
                  </span>
                </div>
              )}
            </Col>
            <Col xs="auto">
              <small className="text-muted">
                <FaClock className="me-1" style={{ fontSize: '0.8rem' }} />
                Expires in {daysRemaining} days
              </small>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Main content - use read-only version */}
      <Container fluid className="report-content">
        <DealAnalysisReadOnly 
          deal={reportData.deal}
          compDetails={reportData.compDetails}
          mlsPhotos={reportData.mlsPhotos}
          compPhotos={reportData.compPhotos}
          listingAgent={reportData.listingAgent}
          propertyDetails={reportData.propertyDetails}
        />
      </Container>

      {/* Comprehensive Footer with Policies */}
      <div className="no-print">
        <FooterWithPolicies />
      </div>
    </div>
  );
};

export default PublicDealReport;